import { useQuery } from "@tanstack/react-query";
import api from "../lib/apiClient";

export interface AuditLog {
  id: string;
  user_id: string | null;
  user_email: string;
  user_name?: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  /**
   * The role the actor had assumed when the action was taken (testing/training
   * role-switch — never set in production). SCRUM-146: surfaced so the trail
   * can show "acting as <role>" instead of falling back to the actor's name.
   */
  assumed_role_name?: string | null;
  /** Subject links — who/what the event is ABOUT (user_id is the actor). */
  resident_id: string | null;
  /**
   * US-167 (F-040): the subject resident's name, resolved server-side across
   * every facility the caller can reach. Consumers must NOT re-derive this from
   * a facility-scoped roster — that silently blanked rows whose resident sat
   * outside the selected facility. `null` means either no resident subject
   * (check `resident_id`) or a name that could not be resolved.
   */
  resident_name?: string | null;
  employee_id: string | null;
  facility_id: string | null;
  group_home_id: string | null;
  /**
   * F-122: the owning organisation's NAME, joined server-side. /admin/audit-logs
   * is the CROSS-ORGANISATION log — its stated purpose is investigating an
   * incident across customers — and a bare group_home_id cannot answer "whose
   * activity is this?" for a reader. Null when the row carries no organisation.
   */
  group_homes?: { name: string } | null;
  description: string | null;
  changes: {
    old?: Record<string, unknown>;
    new?: Record<string, unknown>;
  } | null;
  metadata: {
    ipAddress?: string;
    userAgent?: string;
    [key: string]: unknown;
  } | null;
  created_at: string;
}

export interface AuditLogsFilters {
  userId?: string;
  userEmail?: string;
  resourceType?: string;
  resourceId?: string;
  /** Scope to one resident's events (resident-adjacent resources included). */
  residentId?: string;
  /** Scope to events ABOUT one employee (shifts, training) — subject, not actor. */
  employeeId?: string;
  action?: string;
  excludeActions?: string[]; // Actions to exclude (e.g., ['view'])
  groupHomeId?: string;
  facilityId?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

/**
 * Fetch audit logs with optional filters
 * @param filters - Optional filters for audit logs
 * @param enabled - Whether to enable the query (default: true). Set to false to prevent API call.
 * @param suppressErrorNotification - SCRUM-141: true for secondary/best-effort
 *   widgets (e.g. a resident overview activity card). Confirmed root cause: an
 *   admin testing under "assume role" can legitimately lack access to this data
 *   in the narrower role — an expected 403, not a bug — but it was surfacing as
 *   a page-interrupting toast on top of an otherwise fully-loaded page. Leave
 *   false (default) on primary surfaces like the Audit Logs admin page, where a
 *   load failure IS the page and should stay visible.
 */
export function useAuditLogs(
  filters?: AuditLogsFilters,
  enabled = true,
  suppressErrorNotification = false,
) {
  return useQuery({
    queryKey: ["audit-logs", filters],
    meta: { suppressErrorNotification },
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.userId) params.append("userId", filters.userId);
      if (filters?.resourceType)
        params.append("resourceType", filters.resourceType);
      if (filters?.resourceId) params.append("resourceId", filters.resourceId);
      if (filters?.residentId) params.append("residentId", filters.residentId);
      if (filters?.employeeId) params.append("employeeId", filters.employeeId);
      if (filters?.action) params.append("action", filters.action);
      if (filters?.excludeActions)
        params.append("excludeActions", filters.excludeActions.join(","));
      if (filters?.groupHomeId)
        params.append("groupHomeId", filters.groupHomeId);
      if (filters?.facilityId) params.append("facilityId", filters.facilityId);
      if (filters?.startDate)
        params.append("startDate", filters.startDate.toISOString());
      if (filters?.endDate)
        params.append("endDate", filters.endDate.toISOString());
      if (filters?.limit) params.append("limit", filters.limit.toString());

      const { data } = await api.get(`/admin/audit-logs?${params.toString()}`);
      return data.data as AuditLog[];
    },
    staleTime: 30000, // 30 seconds
    enabled,
  });
}

/**
 * US-152 / F-078 — the audit portal's own, grant-scoped access log.
 *
 * Distinct from the hooks above on purpose. `/audit-logs` is portal:"app" and
 * blocks auditors by design, and `/admin/audit-logs` is the platform's
 * cross-facility view; neither is reachable from the audit portal. This reads
 * the per-facility endpoint, which returns a CURATED projection — who acted,
 * when, what action, which kind of record — filtered row by row against the
 * caller's own grant scopes.
 */
export interface CuratedAuditLogEntry {
  id: string;
  occurred_at: string | null;
  actor_email: string;
  action: string;
  resource_type: string;
  facility_id: string | null;
}

export function useFacilityAuditLogs(facilityId: string | undefined) {
  return useQuery({
    queryKey: ["audit", "facility-audit-logs", facilityId],
    enabled: Boolean(facilityId),
    queryFn: async () => {
      const { data } = await api.get(
        `/audit/facilities/${facilityId}/audit-logs?limit=100`,
      );
      return data.data as { logs: CuratedAuditLogEntry[]; count: number };
    },
  });
}
